import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, Calendar, Clock } from "lucide-react";
import { format, isPast, differenceInDays } from "date-fns";
import { fr } from "date-fns/locale";
import { db } from "@/lib/supabaseUntyped";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface OverdueStep {
  id: string;
  label: string;
  step_type: string;
  due_date: string;
  demarche_id: string;
  demarche_title: string;
}

export function OverdueStepsAlert() {
  const navigate = useNavigate();
  const [overdueSteps, setOverdueSteps] = useState<OverdueStep[]>([]);
  const [upcomingSteps, setUpcomingSteps] = useState<OverdueStep[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOverdueSteps = async () => {
      try {
        const { data: user } = await db.auth.getUser();
        if (!user.user) return;

        // Fetch all incomplete steps with due dates
        const { data, error } = await db
          .from('demarche_steps')
          .select(`
            id,
            label,
            step_type,
            due_date,
            demarche_id,
            demarches!inner(id, title, user_id, status)
          `)
          .eq('is_completed', false)
          .not('due_date', 'is', null)
          .eq('demarches.user_id', user.user.id)
          .neq('demarches.status', 'completed')
          .order('due_date', { ascending: true });

        if (error) throw error;

        const now = new Date();
        const overdue: OverdueStep[] = [];
        const upcoming: OverdueStep[] = [];

        (data || []).forEach((step: any) => {
          const dueDate = new Date(step.due_date);
          const stepData: OverdueStep = {
            id: step.id,
            label: step.label,
            step_type: step.step_type,
            due_date: step.due_date,
            demarche_id: step.demarche_id,
            demarche_title: step.demarches?.title || 'Démarche',
          };

          if (isPast(dueDate)) {
            overdue.push(stepData);
          } else if (differenceInDays(dueDate, now) <= 7) {
            upcoming.push(stepData);
          }
        });

        setOverdueSteps(overdue);
        setUpcomingSteps(upcoming);
      } catch (error) {
        console.error('Error fetching overdue steps:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOverdueSteps();
  }, []);

  if (loading) {
    return <Skeleton className="h-24 rounded-xl mb-6" />;
  }

  if (overdueSteps.length === 0 && upcomingSteps.length === 0) {
    return null;
  }

  const allSteps = [...overdueSteps, ...upcomingSteps];

  return (
    <Card className={cn(
      "mb-6 border-2",
      overdueSteps.length > 0 
        ? "border-destructive/50 bg-destructive/5" 
        : "border-amber-500/50 bg-amber-500/5"
    )}>
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 mb-3">
          {overdueSteps.length > 0 ? (
            <AlertTriangle className="w-5 h-5 text-destructive" />
          ) : (
            <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          )}
          <h3 className={cn(
            "font-semibold",
            overdueSteps.length > 0 ? "text-destructive" : "text-amber-600 dark:text-amber-400"
          )}>
            {overdueSteps.length > 0 
              ? `${overdueSteps.length} étape${overdueSteps.length > 1 ? 's' : ''} en retard`
              : `${upcomingSteps.length} étape${upcomingSteps.length > 1 ? 's' : ''} à venir cette semaine`
            }
          </h3>
        </div>

        <div className="space-y-2">
          {allSteps.slice(0, 5).map((step) => {
            const dueDate = new Date(step.due_date);
            const isOverdue = isPast(dueDate);
            const daysAgo = Math.abs(differenceInDays(new Date(), dueDate));

            return (
              <button
                key={step.id}
                onClick={() => navigate(`/demarches/${step.demarche_id}`)}
                className={cn(
                  "w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors",
                  "hover:bg-background/80"
                )}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{step.label}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {step.demarche_title}
                  </p>
                </div>
                <Badge 
                  variant={isOverdue ? "destructive" : "outline"}
                  className="shrink-0 text-xs gap-1"
                >
                  <Calendar className="w-3 h-3" />
                  {isOverdue 
                    ? `${daysAgo}j de retard`
                    : daysAgo === 0 
                      ? "Aujourd'hui"
                      : `Dans ${daysAgo}j`
                  }
                </Badge>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            );
          })}
        </div>

        {allSteps.length > 5 && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            +{allSteps.length - 5} autre{allSteps.length - 5 > 1 ? 's' : ''} étape{allSteps.length - 5 > 1 ? 's' : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
